import { getCurrentInstance } from 'vue';

import { useStore } from '@martyrs/src/modules/core/views/store/core.store.js';
import { display } from '@martyrs/src/design-system/utils/display.js';
import { formatMoney, getCurrency, isMoney, resolveCurrencyCode, toMinor } from '@martyrs/src/modules/core/models/currencies.js';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

// useGlobalMixins.js
export function useGlobalMixins() {
  // Компонент, который зовёт фабрику из <script setup>. Через него достаём
  // приложение запроса: инстанс vue-i18n свой на каждый SSR-запрос, общего
  // читать негде. Вне setup (globalMixins на уровне модуля) вернёт null.
  const instance = getCurrentInstance();

  const isModuleInstalled = (moduleName) => {
    const store = useStore();
    const options = store.core.state.options;

    if (options && typeof options === 'object') {
      return Object.prototype.hasOwnProperty.call(options, moduleName);
    }

    return false;
  };
  const isAdmin = roles => {
    if (roles) {
      const isAdmin = roles.includes('ROLE_ADMIN') ? roles.includes('ROLE_ADMIN') : false;

      if (isAdmin !== true) {
        return false;
      }

      return true;
    } else {
      return false;
    }
  };

  const hasAccess = (organizationId, rightCategory = null, rightType = null, accesses, roles) => {
    // Если пользователь админ, то сразу даем доступ
    if (isAdmin(roles)) {
      return true;
    }

    const accessArray = accesses;

    // Проверяем существование записи для организации
    const organizationAccess = accessArray.find(access => access.organization === organizationId);

    // Если переданы только organizationId и accesses - проверяем только членство
    if (rightCategory === null && rightType === null) {
      return Boolean(organizationAccess); // если организация найдена - значит член
    }

    // Для проверки прав сначала проверяем существование организации
    if (!organizationAccess) {
      return false;
    }

    const categoryAccess = organizationAccess.rights[rightCategory];
    if (!categoryAccess) {
      return false;
    }
    return categoryAccess[rightType] === true;
  };

  // ISO-код валюты бренда (options.currency). Это НЕ валюта витрины: витрина
  // считается от страны аккаунта (resolveCurrency в models/currencies.js), а
  // валюта бренда — только её запасной вариант для анонима.
  const returnCurrencyCode = () => {
    const store = useStore();
    return resolveCurrencyCode(store.core.state.options?.currency);
  };

  // Символ активной валюты.
  const returnCurrency = () => {
    return getCurrency(returnCurrencyCode()).symbol;
  };

  // money — { amount, currency } или массив таких цен (берётся цена активной
  // валюты). Позиция символа, разделители разрядов и знак минуса — из Intl по
  // локали валюты, руками эти правила не пишем.
  //
  // Legacy-путь: голое число без валюты — считаем его суммой в активной валюте.
  // minor: true — число уже в минорных единицах.
  const formatPrice = (number, { minor = false, currency } = {}) => {
    try {
      // Нет суммы — пустая строка. Шаблоны зовут formatPrice напрямую, и
      // печатать здесь «null» означало бы обвешивать каждый вызов проверкой.
      if (number == null) return '';

      const code = currency || returnCurrencyCode();

      if (isMoney(number) || Array.isArray(number)) {
        return formatMoney(number, { currency: code });
      }

      const amount = minor ? Math.round(number) : toMinor(number, code);
      return formatMoney({ amount, currency: code });
    } catch (err) {
      console.error(err);
      return 'null';
    }
  };
  
  const replaceClasses = (original = '', map = null) => {
    if (map == null && typeof original !== 'string') return '';
    if (typeof map === 'string') {
      const orig = typeof original === 'string' ? original.trim().split(/\s+/) : [];
      const next = map.trim().split(/\s+/);
      const replaceSet = new Set(next);
      const replaced = orig.map(cls => (replaceSet.has(cls) ? next.find(c => c !== cls) || cls : cls));
      const extra = next.filter(c => !orig.includes(c));
      return [...new Set([...replaced, ...extra])].join(' ');
    }

    if (typeof map === 'object' && map !== null) {
      const orig = typeof original === 'string' ? original.trim().split(/\s+/) : [];
      return orig.map(cls => map[cls] ?? cls).join(' ');
    }

    return typeof original === 'string' ? original.trim() : '';
  };
    

  // Обычная функция, а не стрелочная: миксин отдаётся и как метод компонента
  // (methods: useGlobalMixins()), а там язык берётся из `this`.
  function formatDate(d, options = {}) {
    var fixedDate = new Date(d);

    // Гард: невалидная дата (undefined/null/битое значение) не должна ронять
    // рендер — Intl.DateTimeFormat.format(Invalid Date) кидает RangeError и
    // валит всю строку/страницу. Возвращаем пустую строку.
    if (isNaN(fixedDate.getTime())) return '';

    // Определяем параметры локализации
    const formatOptions = {
      year: 'numeric',
      month: 'long', // название месяца
      day: '2-digit', // день с ведущим нулем
      hour: '2-digit', // часы с ведущим нулем
      minute: '2-digit', // минуты с ведущим нулем
      hour12: false, // 24-часовой формат времени
      ...options, // дополнительные настройки
    };

    // Язык из двух источников. instance — вызов из <script setup>: там фабрику
    // зовёт компонент, и через его приложение видно i18n этого запроса. this —
    // вызов через methods глобального миксина: там фабрика отработала на уровне
    // модуля, instance был null, зато метод получает компонент в this.
    const locale = options.language
      || instance?.appContext.config.globalProperties.$i18n?.locale
      || this?.$i18n?.locale
      || 'en';

    // Используем Intl.DateTimeFormat для локализации
    const dateFormatter = new Intl.DateTimeFormat(locale, formatOptions);

    var format = {
      dateOnly: options.dateOnly || false,
      timeOnly: options.timeOnly || false,
      dayMonth: options.dayMonth || false,
      dayTime: options.dayTime || false,
      monthYear: options.monthYear || false,
      yearOnly: options.yearOnly || false,
      custom: options.custom || '',
      monthName: options.monthName || false, // Новый параметр для вывода названия месяца
      relative: options.relative || false, // Отметка времени в ленте: сегодня/вчера/дата
      language: locale,
    };

    if (format.relative) {
      // Отметка времени для строк списков и карточек: сегодня — часы, вчера —
      // словом, раньше — дата числами. Сутки считаем по календарным границам,
      // а не по 24 часам: иначе «вчера в 23:50» показывается как сегодня.
      if (isNaN(fixedDate.getTime())) return '';
      const startOfDay = date => { const s = new Date(date); s.setHours(0, 0, 0, 0); return s; };
      const days = Math.round((startOfDay(new Date()) - startOfDay(fixedDate)) / 86400000);
      if (days <= 0) return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false }).format(fixedDate);
      // numeric: 'auto' даёт «вчера»/«yesterday» словом на языке локали.
      if (days === 1) return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-1, 'day');
      return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }).format(fixedDate);
    } else if (format.dateOnly) {
      return dateFormatter.format(fixedDate).split(',')[0]; // Выводим только дату
    } else if (format.timeOnly) {
      return new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(fixedDate); // Выводим только время
    } else if (format.dayMonth) {
      return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' }).format(fixedDate); // день и месяц
    } else if (format.dayTime) {
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(fixedDate); // день и время
    } else if (format.monthYear) {
      return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(fixedDate); // месяц и год
    } else if (format.yearOnly) {
      return new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(fixedDate); // только год
    } else if (format.custom) {
      // Для пользовательского формата с подстановкой
      return format.custom
        .replace('yyyy', fixedDate.getFullYear())
        .replace('mm', ('0' + (fixedDate.getMonth() + 1)).slice(-2)) // добавление ведущего нуля
        .replace('dd', ('0' + fixedDate.getDate()).slice(-2)) // добавление ведущего нуля
        .replace('hh', ('0' + fixedDate.getHours()).slice(-2)) // добавление ведущего нуля
        .replace('min', ('0' + fixedDate.getMinutes()).slice(-2)); // добавление ведущего нуля
    }

    // Вернуть полную дату в стандартном формате
    return dateFormatter.format(fixedDate);
  }

  const formateText = (text, maxLength = 16) => {
    // Return empty string if text is undefined or null
    if (!text) return '';

    // If text is shorter than or equal to maxLength, return it as is
    if (text.length <= maxLength) {
      return text;
    }

    // Otherwise truncate and add ellipsis
    return text.substring(0, maxLength) + '...';
  };

  const normalizeUrlParam = param => {
    if (!param) param = '';

    return param
      .toLowerCase()
      .replace(/ /g, '-')
      .replace(/[^a-z0-9-]/g, '');
  };

  const joinArrayToUrl = arr => {
    return arr.join('/');
  };

  function generateFilters(selectedFilters) {
    const filters = [];

    // Process all selected filters
    for (const [key, values] of Object.entries(selectedFilters)) {
      if (values && values.length > 0) {
        // Skip special filters (these are handled separately)
        if (key !== 'prices' && key !== 'delivery' && key !== 'categories') {
          filters.push({
            parameter: key,
            caseSensitive: false,
            values: Array.isArray(values) ? values : [values],
          });
        }
      }
    }

    return filters.length > 0 ? JSON.stringify(filters) : undefined;
  }

  const getMarketplaceLink = (categories, country, state, city) => {
    let country_normalized = country ? normalizeUrlParam(country) : '';
    let state_normalized = state ? normalizeUrlParam(state) : '';
    let city_normalized = city ? normalizeUrlParam(city) : '';

    let base = '/marketplace';
    base += country_normalized ? `/${country_normalized}` : '';
    base += country_normalized && state_normalized ? `/${state_normalized}` : '';
    base += country_normalized && state_normalized && city_normalized ? `/${city_normalized}` : '';

    if (categories && categories.length) {
      base += `?categories=${categories.join(',')}`;
    }

    return base;
  };

  const getSpotsLink = (country, state, city) => {
    let country_normalized = country ? normalizeUrlParam(country) : '';
    let state_normalized = state ? normalizeUrlParam(state) : '';
    let city_normalized = city ? normalizeUrlParam(city) : '';

    let base = '/spots';
    base += country_normalized ? `/${country_normalized}` : '';
    base += country_normalized && state_normalized ? `/${state_normalized}` : '';
    base += country_normalized && state_normalized && city_normalized ? `/${city_normalized}` : '';

    return base;
  };

  const isClientSide = typeof window !== 'undefined' && typeof document !== 'undefined';

  const getCssVar = name => {
    if (!isClientSide) return ''; // SSR fallback
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  };

  const matchMediaSafe = query => {
    if (!isClientSide) return { matches: false };
    return window.matchMedia(query);
  };

  const isMediaMax = varName => {
    const maxWidth = getCssVar(varName);
    return matchMediaSafe(`(max-width: ${maxWidth})`).matches;
  };

  const isMediaMin = varName => {
    const minWidth = getCssVar(varName);
    return matchMediaSafe(`(min-width: ${minWidth})`).matches;
  };

  const formatPhone = (phone) => {
    if (!phone) return ''
    const str = String(phone).startsWith('+') ? phone : `+${phone}`
    const parsed = parsePhoneNumberFromString(str)
    return parsed ? parsed.formatInternational() : phone
  }

  // Брейкпоинт-предикаты ПЕРЕЕХАЛИ в дизайн-систему (ds.display, реактивный
  // viewport). Здесь — делегаты для обратной совместимости isPhone()-вызовов;
  // бонус: внутри render/computed они теперь реактивные (читают reactive-state).
  const isFlipPhone = () => display.flipPhone;
  const isPhone = () => display.phone;
  const isTabletPortrait = () => display.tabletPortrait;
  const isTabletLandscape = () => display.tabletLandscape;
  const isTablet = () => display.tablet;
  const isDesktop = () => display.desktop;
  const isXLDesktop = () => display.xlDesktop;
  const isXXLDesktop = () => display.xxlDesktop;
  const isPWA = () => display.pwa;

  return {
    isModuleInstalled,
    isAdmin,
    hasAccess,
    returnCurrency,
    returnCurrencyCode,
    formatPrice,
    formatDate,
    formateText,
    replaceClasses,
    normalizeUrlParam,
    joinArrayToUrl,
    getMarketplaceLink,
    getSpotsLink,
    generateFilters,
    formatPhone,
    // Брейкпоинты
    getCssVar,
    isMediaMax,
    isMediaMin,
    isFlipPhone,
    isPhone,
    isTabletPortrait,
    isTabletLandscape,
    isTablet,
    isDesktop,
    isXLDesktop,
    isXXLDesktop,
    isPWA,
  };
}

// Экспорт для глобальной регистрации
export const globalMixins = {
  methods: useGlobalMixins(),
};
